import { FilterQueryArguments, ProgrammePage } from '@interfaces';

import {
	ProgrammeDateFilter,
	programmeDateFilter,
} from './programmeDateFilter';
import {
	getProgrammeGenreFilter,
	ProgrammeGenreFilter,
} from './programmeGenreFilter';
import {
	ProgrammeTypeFilter,
	programmeTypeFilter,
} from './programmeTypeFilter';
import {
	getProgrammeVenueFilter,
	ProgrammeVenueFilter,
} from './programmeVenueFilter';

// Get filter option: does not return the default (first) option
function getFilterOption(
	filter:
		| ProgrammeGenreFilter
		| ProgrammeDateFilter
		| ProgrammeTypeFilter
		| ProgrammeVenueFilter,
	value: string,
) {
	const optionIndex = filter.options.findIndex(({ filterValue }) => {
		return filterValue === value;
	});

	if (optionIndex > 0) {
		return filter.options[optionIndex];
	}

	return undefined;
}

interface GetQueryArgumentsParams {
	data: URLSearchParams;
	programme: ProgrammePage;
}

export function getQueryArguments({
	data,
	programme,
}: GetQueryArgumentsParams) {
	// The filters for venue and genre come from Dato
	const programmeGenreFilter = getProgrammeGenreFilter(programme);
	const programmeVenueFilter = getProgrammeVenueFilter(programme);

	return Array.from(data).reduce<FilterQueryArguments>(
		(accumulator, [key, value]) => {
			switch (key) {
				// Date filter
				case programmeDateFilter.filterParameter: {
					// Find the filter option with the given value
					const filterOption = programmeDateFilter.options.find(
						(dateFilterOption) => dateFilterOption.filterValue === value,
					);

					// If a filter option is found, add it to the accumulator
					if (filterOption) {
						programmeDateFilter.parameter.forEach((parameter, index) => {
							if (filterOption.value) {
								accumulator[parameter] = filterOption.value[index];
							}
						});
					}

					break;
				}
				// Genre filter
				case programmeGenreFilter?.filterParameter: {
					if (!programmeGenreFilter) break;

					// Find the filter option with the given value
					const filterOption = programmeGenreFilter.options.find(
						(genreFilterOption) => genreFilterOption.filterValue === value,
					);

					// If a filter option is found, add it to the accumulator
					if (filterOption) {
						accumulator.genre = filterOption.filterValue;
					}

					break;
				}
				// Type filter
				case programmeTypeFilter.filterParameter: {
					// Find the filter option with the given value
					const filterOption = programmeTypeFilter.options.find(
						(typeFilterOption) => typeFilterOption.filterValue === value,
					);

					// If a filter option is found, add it to the accumulator
					if (filterOption) {
						accumulator[programmeTypeFilter.filterParameter] =
							filterOption.filterValue;
					}

					break;
				}
				// Venue filter
				case programmeVenueFilter?.filterParameter: {
					if (!programmeVenueFilter) break;

					// Find the filter option with the given value
					const filterOption = programmeVenueFilter.options.find(
						(venueFilterOption) => venueFilterOption.filterValue === value,
					);

					// If a filter option is found, add it to the accumulator
					if (filterOption?.filterValue) {
						accumulator[programmeVenueFilter.filterParameter] =
							filterOption.filterValue;
					}

					break;
				}
				default:
					break;
			}
			return accumulator;
		},
		{},
	);
}

interface GetFilterStateParams {
	data: URLSearchParams;
	programme: ProgrammePage;
}

// Get filterState for URLSearchParams
export function getFilterState({ data, programme }: GetFilterStateParams) {
	const allFilters = [
		programmeDateFilter,
		programmeTypeFilter,
		getProgrammeVenueFilter(programme),
		getProgrammeGenreFilter(programme),
	].filter((filter) => typeof filter !== 'undefined');

	const filteredData = Array.from(data).filter(([key, value]) => {
		const filter = allFilters.find(({ filterParameter }) => {
			return filterParameter === key;
		});

		if (!filter) return false;

		// Only return true if an option is found and it is not the default one
		return !!getFilterOption(filter, value);
	});

	return filteredData;
}

interface GetSearchParamsParams {
	data: FormData;
	programme: ProgrammePage;
}

// Get searchParams for FormData
export function getSearchParams({ data, programme }: GetSearchParamsParams) {
	const allFilters = [
		programmeDateFilter,
		programmeTypeFilter,
		getProgrammeVenueFilter(programme),
		getProgrammeGenreFilter(programme),
	].filter((filter) => typeof filter !== 'undefined');

	const filteredData = Array.from(data)
		.filter(([key, value]) => {
			const filter = allFilters.find(({ filterParameter }) => {
				return filterParameter === key;
			});

			if (!filter) {
				return !!value;
			}

			// Only return true if an option is found and it is not the default one
			return !!getFilterOption(filter, String(value));
		})
		.map(([key, value]) => {
			// Make sure we only have string values (this can probably be done more elegantly)
			return [key, String(value)];
		});

	return new URLSearchParams(filteredData);
}
